import { MapPin, Calendar, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { LOCATION } from "@/constants/testIds";

const IMAGES = {
  bern: "https://images.pexels.com/photos/22180919/pexels-photo-22180919.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  basel: "https://images.pexels.com/photos/27770276/pexels-photo-27770276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  lausanne: "https://images.pexels.com/photos/34006633/pexels-photo-34006633.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const Locations = () => {
  const { t } = useLanguage();
  const cities = [
    { key: "bern", ...t.locations.bern },
    { key: "basel", ...t.locations.basel },
    { key: "lausanne", ...t.locations.lausanne },
  ];

  const scrollContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="locations" className="relative py-24 lg:py-36 bg-anthracite text-cream overflow-hidden">
      <div className="pointer-events-none absolute -top-32 right-10 w-[600px] h-[400px]" style={{ background: "radial-gradient(ellipse, rgba(198,156,34,0.12) 0%, transparent 60%)" }} />
      <div className="container-boe">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <span className="section-eyebrow">{t.locations.eyebrow}</span>
            <h2 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold text-cream leading-[1.05] tracking-tight">
              {t.locations.title}
            </h2>
          </div>
          <p className="text-cream/75 text-lg max-w-md">{t.locations.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {cities.map((city, idx) => (
            <motion.article
              key={city.key}
              data-testid={LOCATION.card(city.key)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              className="group relative rounded-3xl overflow-hidden bg-anthracite-soft border border-gold/20 hover:shadow-2xl hover:shadow-black/40 hover:border-gold/40 transition-all duration-500 hover:-translate-y-2"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={IMAGES[city.key]}
                  alt={city.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-anthracite/95 via-anthracite/40 to-transparent" />
              </div>

              <div className="absolute inset-0 flex flex-col justify-end p-7 lg:p-8 text-cream">
                <div className="flex items-center gap-2 text-gold-soft mb-2" style={{ color: "#E5C76B" }}>
                  <MapPin size={14} />
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold">Schweiz</span>
                </div>
                <h3 className="font-accent italic text-5xl font-medium leading-none mb-3">
                  {city.name}
                </h3>
                <div className="flex items-center gap-2 text-cream/90 text-sm mb-4">
                  <Calendar size={14} />
                  <span>{city.when}</span>
                </div>
                <p className="text-cream/85 text-sm leading-relaxed mb-6 max-w-xs">{city.desc}</p>
                <button
                  onClick={scrollContact}
                  data-testid={LOCATION.visit(city.key)}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-gold-soft hover:text-cream transition-colors w-fit"
                  style={{ color: "#E5C76B" }}
                >
                  {t.locations.visitBtn}
                  <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Locations;
